"use client";

import React, { useEffect, useRef, useState } from "react";
import { BatteryCharging, Download, MapPin, QrCode } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const steps = [{
  number: "01",
  title: "Scan & Rent",
  description: "Locate a ZapGO! station and scan the QR code with our app to unlock a powerbank instantly.",
  icon: QrCode
}, {
  number: "02",
  title: "Charge on the Go",
  description: "Plug in your device and keep moving. Our powerbanks come with built-in cables for all major phones.",
  icon: BatteryCharging
}, {
  number: "03",
  title: "Return Anywhere",
  description: "Done charging? Simply slide the powerbank into any available slot at any ZapGO! station.",
  icon: MapPin
}];
export default function HowItWorks() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="how-it-works" className="relative overflow-hidden bg-background py-24 sm:py-32">
      {}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-20 dark:opacity-40">
        <div className="h-[40rem] w-[40rem] rounded-full bg-primary/20 blur-[100px]" />
      </div>

      <div ref={ref} className={`container relative z-10 mx-auto px-4 md:px-6 transition-all duration-1000 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
        <div className="mx-auto max-w-2xl text-center mb-16 sm:mb-20">
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl mb-4">
            How ZapGO! Works
          </h2>
          <p className="text-lg text-muted-foreground">
            Get fully charged in three simple steps. Fast, contactless, and
            designed to keep you moving.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
          {steps.map((step, i) => {
          const Icon = step.icon;
          return <Card key={step.number} data-index={i} className="group relative overflow-hidden bg-card/40 backdrop-blur-xl border-border/50 shadow-lg hover:border-primary/50 transition-all duration-300 hover:-translate-y-1">
                {}
                <div className="absolute -right-4 -top-8 text-[10rem] font-bold leading-none text-transparent opacity-10 group-hover:opacity-20 transition-opacity duration-500 select-none pointer-events-none" style={{
              WebkitTextStroke: "2px hsl(var(--primary))"
            }} aria-hidden="true">
                  {step.number}
                </div>

                <CardHeader className="relative z-10 pb-4">
                  <div className="mb-6 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-1 ring-primary/20 group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                    <Icon className="size-7" />
                  </div>
                  <CardTitle className="text-2xl">{step.title}</CardTitle>
                </CardHeader>
                <CardContent className="relative z-10">
                  <CardDescription className="text-base text-muted-foreground leading-relaxed">
                    {step.description}
                  </CardDescription>
                </CardContent>
              </Card>;
        })}
        </div>

        <div className="mt-16 sm:mt-20 flex justify-center">
          <Button size="lg" className="rounded-full px-8 h-12 text-base shadow-primary/25 shadow-lg hover:shadow-primary/40 transition-all">
            <Download className="mr-2 size-5" />
            Download Manual
          </Button>
        </div>
      </div>
    </section>;
}